import React, { useState } from 'react';
import ClientJourneyProgressBar from '../../components/ui/ClientJourneyProgressBar';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { TrashIcon } from '@heroicons/react/24/outline';

const defaultJourney = [
  { 
    name: 'Start', 
    color: '#1976d2', 
    steps: [{ 
      name: 'Initial Intake',
      description: 'Client first contact and basic information collection',
      isRequired: true,
      checkpoints: []
    }] 
  },
  { 
    name: 'Finish', 
    color: '#8e24aa', 
    steps: [{ 
      name: 'Case Closure',
      description: 'Final documentation and discharge process',
      isRequired: true,
      checkpoints: []
    }] 
  },
];

function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function JourneyBuilder({ journeyTitle, setJourneyTitle, sections, setSections }) {
  // Add a new section before Finish
  const addSection = () => {
    setSections(secs => [
      ...secs.slice(0, secs.length - 1),
      { 
        name: 'New Section', 
        color: '#ffd600', 
        steps: [{ 
          name: 'New Step',
          description: 'Describe what happens in this step',
          isRequired: false,
          checkpoints: []
        }] 
      },
      secs[secs.length - 1],
    ]);
  };

  // Add a step to a section
  const addStep = (sectionIdx) => {
    setSections(secs => secs.map((sec, idx) =>
      idx === sectionIdx ? { 
        ...sec, 
        steps: [...sec.steps, { 
          name: 'New Step',
          description: 'Describe what happens in this step',
          isRequired: false,
          checkpoints: []
        }] 
      } : sec
    ));
  };

  // Rename section
  const renameSection = (idx, name) => {
    setSections(secs => secs.map((sec, i) => i === idx ? { ...sec, name } : sec));
  };

  // Change section color
  const changeSectionColor = (idx, color) => {
    setSections(secs => secs.map((sec, i) => i === idx ? { ...sec, color } : sec));
  };

  // Rename step
  const renameStep = (sectionIdx, stepIdx, name) => {
    setSections(secs => secs.map((sec, i) =>
      i === sectionIdx ? { 
        ...sec, 
        steps: sec.steps.map((s, j) => 
          j === stepIdx ? { ...s, name } : s
        ) 
      } : sec
    ));
  };

  // Toggle step required status
  const toggleStepRequired = (sectionIdx, stepIdx) => {
    setSections(secs => secs.map((sec, i) =>
      i === sectionIdx ? { 
        ...sec, 
        steps: sec.steps.map((s, j) => 
          j === stepIdx ? { ...s, isRequired: !s.isRequired } : s
        ) 
      } : sec
    ));
  };

  // Update step description
  const updateStepDescription = (sectionIdx, stepIdx, description) => {
    setSections(secs => secs.map((sec, i) =>
      i === sectionIdx ? { 
        ...sec, 
        steps: sec.steps.map((s, j) => 
          j === stepIdx ? { ...s, description } : s
        ) 
      } : sec
    ));
  };

  // Delete section (except Start/Finish)
  const deleteSection = (idx) => {
    if (idx === 0 || idx === sections.length - 1) return;
    setSections(secs => secs.filter((_, i) => i !== idx));
  };

  // Delete step
  const deleteStep = (sectionIdx, stepIdx) => {
    setSections(secs => secs.map((sec, i) =>
      i === sectionIdx ? { ...sec, steps: sec.steps.filter((_, j) => j !== stepIdx) } : sec
    ));
  };

  // Drag and drop handler
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination, type } = result;
    if (type === 'section') {
      // Prevent moving Start/Finish
      if (source.index === 0 || source.index === sections.length - 1 || destination.index === 0 || destination.index === sections.length - 1) return;
      setSections(secs => reorder(secs, source.index, destination.index));
    } else if (type.startsWith('step-')) {
      const sectionIdx = parseInt(type.split('-')[1], 10);
      setSections(secs => secs.map((sec, idx) => {
        if (idx !== sectionIdx) return sec;
        return { ...sec, steps: reorder(sec.steps, source.index, destination.index) };
      }));
    }
  };

  // Flatten steps for progress bar
  const allSteps = sections.flatMap(s => s.steps);
  // Placeholder: current step is the first step
  const currentStep = allSteps[0];

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">Journey Builder</h2>
      <div className="mb-6">
        <label htmlFor="journey-title" className="block text-sm font-medium text-gray-700 mb-1">Journey Title</label>
        <input
          id="journey-title"
          type="text"
          className="w-full border border-gray-300 rounded px-3 py-2 text-lg font-semibold focus:outline-none focus:border-indigo-500"
          placeholder="Enter journey title..."
          value={journeyTitle}
          onChange={e => setJourneyTitle(e.target.value)}
          required
        />
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" direction="vertical" type="section">
          {(provided) => (
            <div className="flex flex-col gap-6" ref={provided.innerRef} {...provided.droppableProps}>
              {sections.map((section, sectionIdx) => (
                <Draggable key={sectionIdx} draggableId={`section-${sectionIdx}`} index={sectionIdx} isDragDisabled={sectionIdx === 0 || sectionIdx === sections.length - 1}>
                  {(sectionProvided, sectionSnapshot) => (
                    <div
                      ref={sectionProvided.innerRef}
                      {...sectionProvided.draggableProps}
                      className={`rounded-xl shadow bg-white border ${sectionSnapshot.isDragging ? 'border-indigo-400' : 'border-gray-200'} p-4`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div {...sectionProvided.dragHandleProps} className="cursor-move text-gray-400 hover:text-indigo-600">
                          <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><circle cx="5" cy="5" r="2" fill="currentColor"/><circle cx="5" cy="15" r="2" fill="currentColor"/><circle cx="15" cy="5" r="2" fill="currentColor"/><circle cx="15" cy="15" r="2" fill="currentColor"/></svg>
                        </div>
                        <input
                          className="font-bold text-lg border-b border-gray-300 bg-transparent focus:outline-none focus:border-indigo-500 flex-1"
                          value={section.name}
                          onChange={e => renameSection(sectionIdx, e.target.value)}
                          style={{ width: 180 }}
                        />
                        <input
                          type="color"
                          value={section.color}
                          onChange={e => changeSectionColor(sectionIdx, e.target.value)}
                          className="ml-2 w-8 h-8 p-0 border-0 bg-transparent cursor-pointer"
                          title="Pick section color"
                        />
                        {sectionIdx !== 0 && sectionIdx !== sections.length - 1 && (
                          <button
                            onClick={() => deleteSection(sectionIdx)}
                            className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full focus:outline-none"
                            aria-label="Delete Section"
                            title="Delete Section"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                      <Droppable droppableId={`steps-${sectionIdx}`} direction="vertical" type={`step-${sectionIdx}`}> 
                        {(stepProvided) => (
                          <div className="flex flex-col gap-2 ml-4" ref={stepProvided.innerRef} {...stepProvided.droppableProps}>
                            {section.steps.map((step, stepIdx) => (
                              <Draggable key={stepIdx} draggableId={`section-${sectionIdx}-step-${stepIdx}`} index={stepIdx}>
                                {(stepDraggableProvided, stepSnapshot) => (
                                  <div
                                    ref={stepDraggableProvided.innerRef}
                                    {...stepDraggableProvided.draggableProps}
                                    className={`flex flex-col gap-2 bg-gray-50 rounded p-2 ${stepSnapshot.isDragging ? 'border border-indigo-400' : ''}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div {...stepDraggableProvided.dragHandleProps} className="cursor-move text-gray-400 hover:text-indigo-600">
                                        <svg width="16" height="16" fill="none" viewBox="0 0 20 20"><circle cx="5" cy="5" r="1.5" fill="currentColor"/><circle cx="5" cy="15" r="1.5" fill="currentColor"/><circle cx="15" cy="5" r="1.5" fill="currentColor"/><circle cx="15" cy="15" r="1.5" fill="currentColor"/></svg>
                                      </div>
                                      <input
                                        className="border-b border-gray-200 bg-transparent focus:outline-none focus:border-indigo-500 flex-1"
                                        value={step.name}
                                        onChange={e => renameStep(sectionIdx, stepIdx, e.target.value)}
                                        style={{ width: 220 }}
                                      />
                                      <label className="flex items-center gap-2 text-sm text-gray-600">
                                        <input
                                          type="checkbox"
                                          checked={step.isRequired}
                                          onChange={() => toggleStepRequired(sectionIdx, stepIdx)}
                                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        Required
                                      </label>
                                      {section.steps.length > 1 && (
                                        <button
                                          onClick={() => deleteStep(sectionIdx, stepIdx)}
                                          className="text-red-400 hover:text-red-600 p-1 rounded-full focus:outline-none"
                                          aria-label="Delete Step"
                                          title="Delete Step"
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      )}
                                    </div>
                                    <textarea
                                      className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-indigo-500"
                                      value={step.description}
                                      onChange={e => updateStepDescription(sectionIdx, stepIdx, e.target.value)}
                                      placeholder="Step description..."
                                      rows={2}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {stepProvided.placeholder}
                            <button onClick={() => addStep(sectionIdx)} className="mt-2 text-indigo-600 hover:underline">+ Add Step</button>
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              <button onClick={addSection} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">+ Add Section</button>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}

export default JourneyBuilder;